import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PaintBrush, X } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

/**
 * One-time coach-mark shown to fresh owners after sign-in nudging them to
 * pick a brand palette on /settings/appearance. Dismiss-once per browser via
 * localStorage `stockaris_palette_coach_dismissed`. Only renders if:
 *   - the user is authenticated AND has owner/admin company role
 *   - the company has not customized branding (still on 'default')
 *   - the coach has not been dismissed yet on this browser
 *   - we're NOT already on the appearance page
 */
export default function PaletteCoachmark() {
  const { user } = useAuth();
  const { companyPalette } = useTheme();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user) return;
    // iter229 — never nudge demo judges to pick a brand palette.
    if (user?.company?.is_demo) return;
    const isOwner = user?.company_role === 'owner' || user?.company_role === 'admin' || user?.role === 'admin';
    if (!isOwner) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem('stockaris_palette_coach_dismissed') === '1'; } catch {/* ignore */}
    if (dismissed) return;
    if (companyPalette && companyPalette !== 'default') return; // already personalized
    if (window.location.pathname.startsWith('/settings/appearance')) return;
    // Show after a short delay so it doesn't compete with the page's first paint
    const t = setTimeout(() => setShow(true), 1800);
    return () => clearTimeout(t);
  }, [user, companyPalette]);

  const dismiss = () => {
    try { localStorage.setItem('stockaris_palette_coach_dismissed', '1'); } catch {/* ignore */}
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="palette-coachmark"
      // Outer wrapper has `pointer-events:none` so it never blocks clicks
      // sitting under the toast (e.g. action buttons in pages that pin
      // their CTAs to the bottom-right). The inner card re-enables
      // pointer events for its own interactive elements.
      className="fixed bottom-6 right-6 z-40 max-w-[340px] pointer-events-none"
    >
      <div className="bg-card border border-border rounded-lg shadow-2xl p-4 animate-in slide-in-from-bottom-3 fade-in duration-300 pointer-events-auto">
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
          data-testid="palette-coachmark-dismiss"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-brand/10 text-brand flex items-center justify-center shrink-0">
            <PaintBrush size={20} weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground text-sm">Make Stockaris feel like home</h3>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Pick a brand accent for your company — it themes the sidebar, buttons, and charts for everyone on your team.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Link
                to="/settings/appearance"
                onClick={dismiss}
                data-testid="palette-coachmark-cta"
                className="inline-flex items-center gap-1 text-xs font-semibold text-brand-foreground bg-brand hover:bg-brand/90 px-3 py-1.5 rounded-md transition-colors"
              >
                Pick a palette →
              </Link>
              <button
                onClick={dismiss}
                className="text-xs font-semibold text-muted-foreground hover:text-foreground"
                data-testid="palette-coachmark-later"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
